import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import gql from "graphql-tag";
import { css } from "@emotion/react";
import { useMutation } from "@apollo/react-hooks";
import { useQuery } from "@apollo/client";
import { isEmpty } from "lodash";
import { Helmet } from "react-helmet";

import Form from "../form/Form";
import LabelledField from "../form/LabelledField";
import Input from "../form/Input";
import FormActions from "../form/FormActions";
import Button from "../Button";
import useForm, { FieldList } from "../../hooks/useForm";
import theme from "../../../theme";
import useToast from "../../hooks/useToast";
import useSetState from "../../hooks/useSetState";
import validationErrorParser, { ValidationErrorType } from "../../includes/validationErrorParser";
import { AUTH_URL, getUrl } from "../../includes/routes";
import { getSiteTitle } from "../../../config/config";

type Props = {
    token: string
};

type DefaultState = {
    viewMode: boolean,
    isSubmitting: boolean,
    areRequirementsVisible: boolean,
}

const INITIAL_STATE = {
    viewMode: false,
    isSubmitting: false,
    areRequirementsVisible: false,
}

let MIN_PASSWORD_LENGTH = 10;

const NewPassword: React.FC<Props> = ({ token }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const redirectTo = location.state?.redirectTo;
    const [successToast] = useToast();
    const [state, setState] = useSetState<DefaultState>(INITIAL_STATE);
    const { data } = useQuery(GET_MIN_PASSWORD_LENGTH);
    const [changePassword] = useMutation(CHANGE_PASSWORD_QUERY);
    const [passwordMatchError, setPasswordMatchError] = useState('');

    const errorRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (data?.minPasswordLength) {
            MIN_PASSWORD_LENGTH = data?.minPasswordLength;
        }
    }, [data]);

    const {
        formState: fields,
        formErrors: errors,
        onFieldChange,
        onFieldInvalid,
        validate,
        setFormErrors
    } = useForm({
        fields: {
            setPassword: '',
            confirmPassword: '',
        },
        validator,
    });

    // Screen reader announces the server error message.
    useEffect(() => {
        if (passwordMatchError && errorRef.current) {
            errorRef.current.focus();
        }
    }, [passwordMatchError]);

    const onFocusPassword = useCallback((e: React.MouseEvent) => {
        if (!state.areRequirementsVisible) {
            setState({ areRequirementsVisible: true });
        }
    }, [setState, state.areRequirementsVisible]);

    const submitChangePasswordForm = useCallback(() => {
        if (validate()) {
            setState({ isSubmitting: true });
            changePassword({
                variables: {
                    ...fields,
                    token
                }
            }).then(() => {
                successToast("Password reset is complete.");
                navigate(redirectTo ? redirectTo : getUrl(AUTH_URL));
            }).catch((e: any) => {
                setState({ isSubmitting: false });

                const validationErrors: ValidationErrorType = validationErrorParser(e);
                if (!isEmpty(validationErrors)) {
                    setFormErrors(validationErrors);
                }

                if (validationErrors["errorMessage"]) {
                    setPasswordMatchError(validationErrors["errorMessage"]);
                }
            });
        }
    }, [changePassword, fields, token, validate, setFormErrors, setState, successToast, navigate, redirectTo]);

    const headerStyle = css`
        display: flex;
        justify-content: space-between;
    `;

    const leftHeaderStyle = css`
        margin: 0;
        color: ${theme.colours.curiousBlue};
        font-weight: ${theme.fonts.weights.bold};

        ${theme.breakpoints.up("xxl")} {
            font-size: 50px;
            line-height: 75px;
        }

        ${theme.breakpoints.between("md", "xl")} {
            font-size: 43px;
            line-height: 65px;
        }

        ${theme.breakpoints.down("sm")} {
            font-size: 30px;
            line-height: 45px;
        }
    `;

    const passwordResetContentStyle = css`
        margin-top: 40px;
        width: 510px;
        position: relative;
    `;

    const leftDescriptionStyle = css`
        margin-bottom: 10px;
        font-size: 18px;
        font-weight: 400;
    `;

    const formContainerStyle = css`
        margin-top: 12px;
    `;

    const passwordInputStyle = css`
        margin-top: 0;
        box-sizing: border-box;
        width: 100%;
    `;

    const passwordRequirementsStyle = css`
        margin-bottom: 19px;

        p {
            margin-bottom: 10px;
            font-size: 14px;
        }
    `;

    const isPasswordLongEnough = (password: string): boolean => password.length >= 10;

    const passwordRequirement = (password: string, fieldHasError: boolean) => css`
        font-size: 14px;
        font-style: italic;
        list-style: disc inside;
        color: ${password === '' && !fieldHasError ?
                'black' :
                isPasswordLongEnough(password)
                        ? theme.colours.killarney
                        : theme.colours.crimson

        }
    `;

    const formActionsStyle = css`
        width: 100%;
        display: flex;
        flex-direction: row;
        margin-top: 20px;

        & .action-buttons button {
            width: 110px;
            min-width: 100px;
        }
    `;

    const errorMessageStyle = css`
        display: ${passwordMatchError ? 'flex' : 'none'};
        padding: 10px;
        width: 100%;
        background-color: ${theme.colours.chablis};
        border-radius: 3px;
        border: 1px solid ${theme.colours.crimson};
        color: ${theme.colours.red[999]};
        margin-top: 6px;
    `;

    return (
        <>
            <Helmet>
                <title>{getSiteTitle("Change Password")}</title>
            </Helmet>
            <div css={passwordResetContentStyle}>
                <div css={headerStyle}>
                    <h1 css={leftHeaderStyle}>Change Password</h1>
                </div>
                <div css={leftDescriptionStyle}>Please provide a new password</div>
                <div css={errorMessageStyle} ref={errorRef} role={"alert"}>
                    <span>{passwordMatchError}</span>
                </div>
                <div css={formContainerStyle}>
                    <Form onSubmit={submitChangePasswordForm}>
                        <LabelledField name="setPassword" error={errors.setPassword} label="New Password">
                            <Input
                                type="password"
                                hasError={!!errors.setPassword}
                                css={passwordInputStyle}
                                label="Enter new password"
                                name="setPassword"
                                value={fields.setPassword}
                                onInvalid={onFieldInvalid}
                                onFocus={onFocusPassword}
                                onChange={onFieldChange}
                                required
                            />
                        </LabelledField>
                        {state.areRequirementsVisible && (
                            <div css={passwordRequirementsStyle}>
                                <p>Password must:</p>
                                <ul>
                                    <li css={passwordRequirement(fields.setPassword, !!errors.setPassword)}>
                                        Password must have at least {MIN_PASSWORD_LENGTH} characters.
                                    </li>
                                </ul>
                            </div>
                        )}
                        <LabelledField
                            name="confirmPassword"
                            error={errors.confirmPassword}
                            label="Confirm New Password"
                        >
                            <Input
                                type="password"
                                hasError={!!errors.confirmPassword}
                                css={passwordInputStyle}
                                label="Enter confirm new password"
                                name="confirmPassword"
                                onChange={onFieldChange}
                                onInvalid={onFieldInvalid}
                                value={fields.confirmPassword}
                                required
                            />
                        </LabelledField>
                        <div css={formActionsStyle}>
                            <FormActions>
                                <div className={"action-buttons"}>
                                    <Button
                                        type="submit"
                                        forceLoading={state.isSubmitting}
                                        useOutline
                                    >
                                        Save
                                    </Button>
                                </div>
                            </FormActions>
                        </div>
                    </Form>
                </div>
            </div>
        </>
    );
};

const CHANGE_PASSWORD_QUERY: any = gql`
    mutation recoverPasswordHomeUser($setPassword: String!, $confirmPassword: String!, $token: String!) {
        recoverPasswordHomeUser(setPassword: $setPassword, confirmPassword: $confirmPassword, token: $token) {
            id
        }
    }
`;

const GET_MIN_PASSWORD_LENGTH = gql`
    query minPasswordLength {
        minPasswordLength(isStudent: false)
    }
`;

type PasswordError = {
    setPassword?: string,
    confirmPassword?: string
};

const validator = (fields: FieldList) => {
    const errors: PasswordError = {};

    if (!fields.setPassword) {
        errors.setPassword = 'Please provide a new password';
    }

    if (!fields.confirmPassword) {
        errors.confirmPassword = 'Please confirm your new password';
    }

    return errors;
};


export default NewPassword;
